// The JSON Schema of the kit's piece definitions (spike 002 C4), so a game's content table of pieces is checked
// against the kit's own shapes: one branch per archetype, each with the game's extra properties (an id, a reason).
// The package owns the shapes and their sane ranges; the game owns the values.

/** A JSON Schema object. */
export type KitSchema = Record<string, unknown>;

const num = (minimum: number, maximum: number, description?: string): KitSchema => ({ type: "number", minimum, maximum, ...(description ? { description } : {}) });
const range = (minimum: number, maximum: number, description: string): KitSchema => ({
  description: `${description} A number, or [min, max] for the seed to pick in.`,
  anyOf: [num(minimum, maximum), { type: "array", items: num(minimum, maximum), minItems: 2, maxItems: 2 }],
});
const colour: KitSchema = { type: "string", minLength: 1, description: "A palette key, or a #hex colour." };
const colours: KitSchema = { type: "array", items: colour, minItems: 1, maxItems: 8 };
const obj = (properties: Record<string, KitSchema>, required: string[]): KitSchema => ({ type: "object", additionalProperties: false, required, properties });

const FURNITURE = obj(
  {
    kind: { enum: ["table", "bench", "bed", "shelf", "counter", "forge", "anvil", "barrel", "crate", "hearth", "rug"] },
    at: { type: "array", items: num(-1, 1), minItems: 2, maxItems: 2, description: "Where in the room: [u across x, v across z], −1..1." },
    yaw: num(-6.3, 6.3),
    colour,
    accent: colour,
    highOnly: { type: "boolean", description: "Only on the high tier." },
  },
  ["kind", "at"],
);

/** Each archetype's fields (beside `kind`) and which are required. */
const KINDS: Record<string, { properties: Record<string, KitSchema>; required: string[]; description: string }> = {
  house: {
    description: "A house with walls, a gable roof, windows that glow at night, a door, and maybe a walkable room.",
    properties: {
      width: range(2, 20, "Width along x (m)."),
      depth: range(2, 20, "Depth along z (m); the door is on the +z side."),
      wallHeight: range(2, 8, "Wall height (m)."),
      plinth: num(0, 0.34, "The floor's height above the ground (m): under a character's step height."),
      wall: colours,
      stone: colour,
      timber: colour,
      roof: obj({ pitch: range(0.2, 1.2, "Roof pitch (rad)."), overhang: num(0, 1.5), colours }, ["pitch", "overhang", "colours"]),
      chimney: obj({ chance: num(0, 1), colour }, ["chance", "colour"]),
      door: obj({ width: num(0.9, 3), height: num(1.9, 3.5), colour }, ["width", "height", "colour"]),
      windows: obj(
        { perSide: range(0, 6, "Windows per wall."), width: num(0.3, 2), height: num(0.3, 2), frame: colour, glass: colour, litChance: num(0, 1, "The chance a window glows at night.") },
        ["perSide", "width", "height", "frame", "glass", "litChance"],
      ),
      awning: obj({ depth: num(0.5, 3), colours }, ["depth", "colours"]),
      interior: obj({ profile: { type: "string" }, light: { type: "string" }, floor: colour, furniture: { type: "array", items: FURNITURE, maxItems: 16 } }, ["profile", "light", "floor", "furniture"]),
      farDistance: num(10, 500, "From this distance (m) on, the far form."),
    },
    required: ["width", "depth", "wallHeight", "plinth", "wall", "stone", "roof", "door", "windows"],
  },
  ruin: {
    description: "A ruined wall, arch, column or gate of stone courses, broken by the seed.",
    properties: {
      form: { enum: ["wall", "arch", "column", "gate"] },
      length: range(0.5, 20, "Wall length, or the arch's or gate's span (m)."),
      height: range(0.5, 10, "Height (m)."),
      thickness: num(0.2, 2),
      stone: colours,
      moss: colour,
      broken: num(0, 1, "0 whole .. 1 badly broken."),
    },
    required: ["form", "length", "height", "thickness", "stone", "broken"],
  },
  bridge: {
    description: "An arched plank footbridge with rails, spanning along z.",
    properties: { span: num(2, 30), width: num(1, 6), rise: num(0, 3), deck: colour, rail: colour, stone: colour },
    required: ["span", "width", "rise", "deck", "rail", "stone"],
  },
  fence: {
    description: "A run of fence along x.",
    properties: { length: num(0.5, 30), height: num(0.4, 2), spacing: num(0.5, 4), colour },
    required: ["length", "height", "spacing", "colour"],
  },
  well: {
    description: "A stone well with a little roof.",
    properties: { radius: num(0.4, 2), height: num(0.4, 1.5), stone: colours, wood: colour, roof: colour, water: colour },
    required: ["radius", "height", "stone", "wood", "roof", "water"],
  },
  stall: {
    description: "A market stall: a counter, posts, a striped awning, goods.",
    properties: { width: num(1, 5), depth: num(0.6, 3), wood: colour, awning: colours, goods: colours },
    required: ["width", "depth", "wood", "awning", "goods"],
  },
  lantern: {
    description: "A lantern on a post that glows at night.",
    properties: { height: num(1, 5), post: colour, glass: colour },
    required: ["height", "post", "glass"],
  },
  crate: { description: "A crate.", properties: { colour, band: colour }, required: ["colour"] },
  barrel: { description: "A barrel.", properties: { colour, band: colour }, required: ["colour"] },
};

/**
 * The schema of a content row holding one piece definition: one branch per archetype (`kind`), each with the game's
 * own `properties` (an id, a reason) beside the kit's fields.
 */
export function kitPieceSchema(o: { title: string; description: string; properties: Record<string, KitSchema>; required: string[] }): KitSchema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: o.title,
    description: o.description,
    oneOf: Object.entries(KINDS).map(([kind, k]) => ({
      type: "object",
      title: `${o.title}${kind[0]!.toUpperCase()}${kind.slice(1)}`,
      description: k.description,
      additionalProperties: false,
      required: [...o.required, "kind", ...k.required],
      properties: { ...o.properties, kind: { const: kind }, ...k.properties },
    })),
  };
}
